import { useCallback, useEffect, useState } from "react";

const SESSION_MUTE_KEY = "globalVideoMuted";
const EVENT_NAME = "global-video-mute-change";

const readStoredMuted = (): boolean => {
  try {
    const value = sessionStorage.getItem(SESSION_MUTE_KEY);
    return value === null ? true : value === "true";
  } catch {
    return true;
  }
};

export interface GlobalMuteEventDetail {
  muted: boolean;
}

export const useGlobalVideoMute = () => {
  const [isMuted, setIsMuted] = useState<boolean>(readStoredMuted());

  useEffect(() => {
    // Sync state with latest stored value on mount
    setIsMuted(readStoredMuted());

    const handleGlobalMuteChange = (e: Event) => {
      const ce = e as CustomEvent<GlobalMuteEventDetail>;
      setIsMuted(ce.detail.muted);
    };

    const handleStorage = (e: StorageEvent) => {
      if (e.key === SESSION_MUTE_KEY && e.storageArea === sessionStorage) {
        const next = e.newValue === null ? true : e.newValue === "true";
        setIsMuted(next);
      }
    };

    window.addEventListener(EVENT_NAME, handleGlobalMuteChange as EventListener);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, handleGlobalMuteChange as EventListener);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const setMuted = useCallback((muted: boolean) => {
    try {
      sessionStorage.setItem(SESSION_MUTE_KEY, String(muted));
    } catch (err) {
      void err;
    }
    // Update local state first for immediate UI response
    setIsMuted(muted);
    // Broadcast to all listeners
    window.dispatchEvent(
      new CustomEvent<GlobalMuteEventDetail>(EVENT_NAME, { detail: { muted } }),
    );
  }, []);

  return { isMuted, setMuted } as const;
};

export default useGlobalVideoMute;
